import { forwardRef, Fragment } from 'react'
import PropTypes from 'prop-types'

import { FORM_SECTION_IDS, FORM_SECTIONS } from '../../Cfmp.constants'
import { Container, Divider } from '../../Cfmp.styles'
import { useCfmpForm } from './CfmpForm.hook'
import { sourceShape } from './CfmpForm.propTypes'
import { CfmpFormAdministrativeAct } from './CfmpFormAdministrativeAct'
import { CfmpFormAttachments } from './CfmpFormAttachments'
import { CfmpFormGeneralInformation } from './CfmpFormGeneralInformation'
import { CfmpFormJustification } from './CfmpFormJustification'
import CfmpFormSection from './CfmpFormSection'
import { CfmpFormSupplierIdentification } from './CfmpFormSupplierIdentification'
import { CfmpFormSustainablePurchase } from './CfmpFormSustainablePurchase'

const SECTION_COMPONENTS = {
  [FORM_SECTION_IDS.JUSTIFICATION]: CfmpFormJustification,
  [FORM_SECTION_IDS.ADMINISTRATIVE_ACT]: CfmpFormAdministrativeAct,
  [FORM_SECTION_IDS.SUPPLIER_IDENTIFICATION]: CfmpFormSupplierIdentification,
  [FORM_SECTION_IDS.GENERAL_INFORMATION]: CfmpFormGeneralInformation,
  [FORM_SECTION_IDS.ATTACHMENTS]: CfmpFormAttachments,
  [FORM_SECTION_IDS.SUSTAINABLE_PURCHASE]: CfmpFormSustainablePurchase
}

/**
 * The CfmpForm component.
 */
export const CfmpForm = forwardRef(
  (
    {
      attachedFiles = [],
      isDisabled = false,
      isSourcesLoading = false,
      onAddFile,
      onOpenMoreInfoDialog,
      onRemoveFile,
      sources
    },
    ref
  ) => {
    const {
      administrativeActDate,
      administrativeActName,
      administrativeActNumber,
      administrativeActType,
      administrativeActTypeDetail,
      attachmentsErrors,
      contractEndDate,
      contractStartDate,
      currency,
      environmentalPurchase,
      procedureType,
      procedureTypeDetail,
      purchaseReason,
      reasonType,
      socialEconomicPurchase,
      supplierName,
      supplierRut,
      totalAmount
    } = useCfmpForm({ attachedFiles, ref, sources })

    const SECTION_PROPS = {
      [FORM_SECTION_IDS.JUSTIFICATION]: { reasonType },
      [FORM_SECTION_IDS.ADMINISTRATIVE_ACT]: {
        administrativeActDate,
        administrativeActName,
        administrativeActNumber,
        administrativeActType,
        administrativeActTypeDetail,
        procedureType,
        procedureTypeDetail
      },
      [FORM_SECTION_IDS.SUPPLIER_IDENTIFICATION]: {
        procedureType,
        reasonType,
        supplierName,
        supplierRut
      },
      [FORM_SECTION_IDS.GENERAL_INFORMATION]: {
        contractEndDate,
        contractStartDate,
        currency,
        purchaseReason,
        totalAmount
      },
      [FORM_SECTION_IDS.ATTACHMENTS]: {
        attachedFiles,
        errors: attachmentsErrors,
        onAdd: onAddFile,
        onRemove: onRemoveFile
      },
      [FORM_SECTION_IDS.SUSTAINABLE_PURCHASE]: {
        environmentalPurchase,
        onOpenMoreInfoDialog,
        socialEconomicPurchase
      }
    }

    return (
      <Container direction="column" spacing={4}>
        {FORM_SECTIONS.map(({ description, id, title }, index) => {
          const SectionContent = SECTION_COMPONENTS[id]

          return (
            <Fragment key={id}>
              <CfmpFormSection description={description} title={title}>
                <SectionContent
                  isDisabled={isDisabled}
                  isSourcesLoading={isSourcesLoading}
                  {...SECTION_PROPS[id]}
                />
              </CfmpFormSection>

              {index < FORM_SECTIONS.length - 1 && <Divider />}
            </Fragment>
          )
        })}
      </Container>
    )
  }
)

CfmpForm.displayName = 'CfmpForm'

CfmpForm.propTypes = {
  attachedFiles: PropTypes.array,
  isDisabled: PropTypes.bool,
  isSourcesLoading: PropTypes.bool,
  onAddFile: PropTypes.func.isRequired,
  onOpenMoreInfoDialog: PropTypes.func.isRequired,
  onRemoveFile: PropTypes.func.isRequired,
  sources: PropTypes.shape({
    administrativeActTypes: sourceShape.isRequired,
    currencies: sourceShape.isRequired,
    procedureTypes: sourceShape.isRequired,
    reasonTypes: sourceShape.isRequired
  }).isRequired
}
